import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skull, Zap } from "lucide-react";
import { fetchCharacters } from "@/lib/api";
import SectionHeading from "@/components/common/SectionHeading";

/**
 * "The Nemesis" — Goliath spotlight.
 * Renders only when a villain exists in the DB. Uses a dark violet
 * palette so it sits opposite the hero grid without recycling the
 * hero colors (red/blue/gold/green).
 */
export default function NemesisSection() {
  const { data: villains = [], isLoading } = useQuery({
    queryKey: ["characters", "villains"],
    queryFn: () => fetchCharacters("villains"),
  });

  if (isLoading || villains.length === 0) return null;
  const goliath = villains[0];

  return (
    <section
      className="relative py-24 overflow-hidden bg-[hsl(var(--kotz-ink))]"
      data-testid="nemesis-section"
    >
      {/* Ambient nightmare violet aura */}
      <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-[radial-gradient(circle,hsla(265,65%,55%,0.18),transparent_60%)] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 h-[500px] w-[500px] rounded-full bg-[radial-gradient(circle,hsla(142,65%,45%,0.12),transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="The Nemesis"
          title="EVERY HERO NEEDS ONE."
          subtitle="Meet the shadow that stalks the Kotzinons across the stars."
          align="center"
          className="mx-auto mb-12"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Card
            className="relative overflow-hidden rounded-3xl border border-[hsl(var(--kotz-purple))]/35 bg-[hsl(var(--kotz-ink-2))]"
            data-testid={`nemesis-card-${goliath.slug}`}
          >
            <div className="grid lg:grid-cols-12 gap-0">
              {/* Image */}
              <div className="lg:col-span-6 relative">
                <div className="aspect-[4/5] lg:aspect-auto lg:h-full w-full overflow-hidden bg-black">
                  <img
                    src={goliath.image_url}
                    alt={goliath.name}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--kotz-ink))] via-transparent to-transparent lg:bg-gradient-to-r pointer-events-none" />
                  {/* Toxic pulse */}
                  <motion.div
                    className="absolute inset-0 pointer-events-none mix-blend-screen"
                    style={{
                      boxShadow:
                        "inset 0 0 100px hsla(265, 65%, 55%, 0.3), inset 0 0 200px hsla(142, 65%, 45%, 0.15)",
                    }}
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="lg:col-span-6 p-8 lg:p-12 flex flex-col justify-center">
                <Badge
                  variant="outline"
                  className="bg-[hsl(var(--kotz-purple))]/15 text-[hsl(var(--kotz-purple))] border-[hsl(var(--kotz-purple))]/50 font-mono text-[10px] tracking-[0.25em] uppercase w-fit"
                >
                  <Skull className="h-3 w-3 mr-1" /> Arch Nemesis
                </Badge>

                <h3
                  className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl tracking-widest text-foreground leading-[0.9]"
                  style={{
                    textShadow:
                      "0 0 40px hsla(265, 65%, 55%, 0.35), 0 0 20px hsla(142, 65%, 45%, 0.15)",
                  }}
                >
                  {goliath.name.toUpperCase()}
                </h3>

                <p className="mt-3 font-serif italic text-lg text-foreground/75">
                  {goliath.tagline}
                </p>

                <p className="mt-5 text-base text-foreground/70 leading-relaxed">
                  {goliath.description}
                </p>

                {/* Weapon + abilities */}
                <div className="mt-6 space-y-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-foreground/45">
                      Signature Power
                    </div>
                    <div className="mt-1 inline-flex items-center gap-2 text-sm text-foreground">
                      <Zap className="h-4 w-4 text-[hsl(var(--kotz-green))]" />
                      {goliath.weapon}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-foreground/45">
                      Capabilities
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {goliath.abilities.map((a) => (
                        <Badge
                          key={a}
                          variant="outline"
                          className="text-[10px] font-mono uppercase tracking-wider border-[hsl(var(--kotz-purple))]/30 text-foreground/75 bg-[hsl(var(--kotz-purple))]/10"
                        >
                          {a}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}
